import React from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import Spinner from "./Spinner";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import filter from "../images/filter.svg";
import { withNamespaces } from "react-i18next";

const MyTable = React.forwardRef(
  (
    {
      columns,
      data,
      totalCount,
      tableTitle,
      initialPageIndex = 0,
      initialPageSize = 10,
      sortDirection,
      sortBy,
      onSortChange,
      onPageChange,
      onPageSizeChange, // Define onPageSizeChange function
      loading,
      hidePagination = false,
      tableStyle,
      //Pass a function to toggle filter drawer and show filter icon
      toggleFilterDrawer = undefined,
      t,
    },
    ref
  ) => {
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      page,
      prepareRow,
      canPreviousPage,
      canNextPage,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      state: { pageIndex, pageSize },
    } = useTable(
      {
        columns,
        data: data || [],
        initialState: {
          pageIndex: initialPageIndex - 1,
          pageSize: initialPageSize,
          sortBy: [
            {
              id: sortBy,
              desc: sortDirection === "desc",
            },
          ],
        },
        manualPagination: true, // Enable manual pagination
        manualSortBy: true, // Enable manual sorting
        pageCount: Math.ceil(totalCount / initialPageSize), // Calculate pageCount based on totalCount and initialPageSize
      },
      useSortBy,
      usePagination
    );

    const generatePageNumbers = () => {
      const maxPagesToShow = 3;
      const pageCount = Math.ceil(totalCount / pageSize);
      let pages = [];

      if (pageCount <= maxPagesToShow + 2) {
        pages = Array.from({ length: pageCount }, (_, i) => i + 1);
      } else {
        if (pageIndex < maxPagesToShow) {
          pages = [
            ...Array.from({ length: maxPagesToShow }, (_, i) => i + 1),
            "...",
            pageCount,
          ];
        } else if (pageIndex >= pageCount - maxPagesToShow) {
          pages = [
            1,
            "...",
            ...Array.from(
              { length: maxPagesToShow },
              (_, i) => pageCount - maxPagesToShow + 1 + i
            ),
          ];
        } else {
          pages = [
            1,
            "...",
            ...Array.from(
              { length: maxPagesToShow },
              (_, i) => pageIndex - Math.floor(maxPagesToShow / 2) + 1 + i
            ),
            "...",
            pageCount,
          ];
        }
      }

      return pages;
    };
    const pageNumbers = generatePageNumbers();

    return (
      <>
        <div className={`relative ${tableStyle ? tableStyle : ""}`}>
          <div className={`p-5 bg-white_50 rounded-[14px] mb-[22px]`}>
            {tableTitle && (
              <h4 className=" pb-5 text-gray_900 text-lg font-normal mb-3">
                {tableTitle}
              </h4>
            )}
            <div
              className={`scroll-container h-full ${
                !loading ? "overflow-auto" : "overflow-hidden"
              }`}
              ref={ref}
            >
              <table
                {...getTableProps()}
                className="w-full border-collapse h-full custom-company-table"
              >
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          className="text-left company-heading text-nowrap pb-3 px-[10px] text-gray_900 font-semibold text-sm"
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                          onClick={() =>
                            !column?.disableSortBy && onSortChange(column?.key)
                          }
                        >
                          <div className="flex items-center group font-lexend">
                            {column.render("Header")}
                            {!column.disableSortBy && sortBy === column?.key ? (
                              sortDirection === "desc" ? (
                                <FaArrowDown className="ml-2 opacity-0 group-hover:opacity-100" />
                              ) : (
                                <FaArrowUp className="ml-2 opacity-0 group-hover:opacity-100" />
                              )
                            ) : (
                              ""
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                {!loading && (
                  <tbody {...getTableBodyProps()}>
                    {page.length > 0 ? (
                      page.map((row) => {
                        prepareRow(row);
                        return (
                          <tr
                            className="border-b border-gray_100"
                            {...row.getRowProps()}
                          >
                            {row.cells.map((cell) => (
                              <td
                                className="py-2.5 px-[10px] text-nowrap  text-gray_900 font-normal text-sm"
                                {...cell.getCellProps()}
                              >
                                {cell.render("Cell")}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="text-center py-5"
                        >
                          {t("common_table.no_records_found")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </table>
            </div>
            {loading && <Spinner size="md" color="#C77025" />}
          </div>

          {!hidePagination && !loading && (
            <div className="pagination flex justify-between items-center mt-5">
              <button
                type="button"
                className="text-gray_500 font-pop text-sm flex items-center"
                onClick={() => {
                  previousPage();
                  onPageChange(pageIndex);
                }}
                disabled={!canPreviousPage}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.8334 10.0001H4.16669M4.16669 10.0001L10 15.8334M4.16669 10.0001L10 4.16675"
                    stroke="#797171"
                    stroke-width="1.67"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span className="block ml-2">{t("common_table.previous")}</span>
              </button>

              <div className="flex items-center">
                {pageNumbers.map((page, index) => (
                  <button
                    type="button"
                    key={index}
                    className={`px-2 py-1 rounded-lg w-10 h-10 ${
                      pageIndex === page - 1
                        ? "bg-primary_orange text-white"
                        : "bg-transparent text-gray_400"
                    }`}
                    onClick={() => {
                      if (typeof page === "number") {
                        gotoPage(page - 1);
                        onPageChange(page);
                      }
                    }}
                    disabled={page === "..."}
                  >
                    {page === "..." ? "..." : page}
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="text-gray_500 font-pop text-sm flex items-center"
                onClick={() => {
                  nextPage();
                  onPageChange(pageIndex + 2);
                }}
                disabled={!canNextPage}
              >
                <span className="block mr-2">{t("common_table.next")}</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.16669 10.0001H15.8334M15.8334 10.0001L10 4.16675M15.8334 10.0001L10 15.8334"
                    stroke="#797171"
                    stroke-width="1.67"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>

              <select
                className={`absolute top-5 bg-gray-50 py-2 px-4 rounded-lg ${
                  toggleFilterDrawer ? "right-16" : "right-5"
                }`}
                value={pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  setPageSize(newSize);
                  gotoPage(0);
                  onPageSizeChange(newSize); // Call onPageSizeChange function
                  // styles = { customStylesDropdown };
                }}
              >
                {[5, 10, 20, 30, 40, 50].map((size) => (
                  <option key={size} value={size}>
                    {t("common_table.show")} {size}
                  </option>
                ))}
              </select>
              {toggleFilterDrawer && (
                <div className="absolute top-5 right-5 py-2 flex">
                  <button onClick={toggleFilterDrawer}>
                    <img src={filter} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  }
);

export default withNamespaces()(MyTable);
